import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Login } from "@/components/auth/Login";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';

interface InvitationMetadata {
  projectId?: string;
  permission?: 'view' | 'edit';
  inviterEmail?: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('id');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Fetch invitation details first
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!invitationId) {
        setError(t('invalid_invitation_link'));
        return;
      }

      try {
        const { data, error } = await supabase
          .from('invitation_links')
          .select('*, projects!inner(*)')
          .eq('id', invitationId)
          .single();

        if (error) {
          console.error('Error fetching invitation:', error);
          setError(t('invalid_or_expired_invitation_link'));
          return;
        }

        if (!data.active || data.uses >= data.max_uses) {
          setError(t('invitation_expired'));
          return;
        }

        if (data.expires_at && new Date(data.expires_at) < new Date()) {
          setError(t('invitation_expired'));
          return;
        }

        setInvitation(data);
      } catch (error) {
        console.error('Error:', error);
        setError(t('failed_to_load_invitation'));
      } finally {
        setIsLoadingInvitation(false);
      }
    };

    fetchInvitation();
  }, [invitationId, navigate]);

  // Process the invitation once user is authenticated
  useEffect(() => {
    const acceptInvitation = async () => {
      if (!user || !invitation || isProcessing) return;
      
      setIsProcessing(true);
      try {
        console.log('Processing invitation:', invitationId);
        
        const metadata = invitation.metadata as InvitationMetadata;
        console.log('Invitation metadata:', metadata);

        if (!metadata?.projectId) {
          throw new Error("Invalid invitation: missing project ID");
        }

        // Check if user already has access to the project
        const { data: existingShares } = await supabase
          .from('project_shares')
          .select('id')
          .eq('project_id', metadata.projectId)
          .eq('shared_with', user.id)
          .limit(1);

        if (existingShares && existingShares.length > 0) {
          toast.info(t('already_have_access'));
          navigate("/");
          return;
        }

        // Create project share
        const { error: shareError } = await supabase
          .from('project_shares')
          .insert({
            project_id: metadata.projectId,
            shared_with: user.id,
            shared_by: invitation.created_by,
            permission: metadata.permission || 'view'
          });

        if (shareError) {
          console.error('Error creating project share:', shareError);
          throw shareError;
        }

        // Update invitation uses
        const { error: updateError } = await supabase
          .from('invitation_links')
          .update({ 
            uses: invitation.uses + 1,
            active: invitation.uses + 1 >= invitation.max_uses ? false : true
          })
          .eq('id', invitationId);

        if (updateError) {
          console.error('Error updating invitation:', updateError);
          throw updateError;
        }

        toast.success(t('added_to_project'));
        navigate("/");
      } catch (error) {
        console.error('Error accepting invitation:', error);
        setError(t('failed_to_accept_invitation'));
      } finally {
        setIsProcessing(false);
      }
    };

    acceptInvitation();
  }, [user, invitation, invitationId, isProcessing, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1A1F2C] to-[#2d1f3d]">
        <div className="max-w-md w-full mx-auto px-4">
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate("/")} className="w-full">
            {t('go_to_homepage')}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1A1F2C] to-[#2d1f3d]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg">{t('loading_invitation')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1F2C] to-[#2d1f3d]">
        <div className="max-w-md mx-auto pt-8 px-4">
          <div className="glass-card rounded-lg p-8 backdrop-blur-xl border border-white/5 mb-8">
            <h1 className="text-2xl font-bold mb-2">{t('welcome_to_prept')}</h1>
            <p className="text-muted-foreground mb-4">
              {t('invited_to_collaborate_on')}{" "}
              <span className="font-semibold text-purple-400">
                {invitation?.projects?.name || t('a_project')}
              </span>
              {invitation?.metadata?.inviterEmail && (
                <> {t('by')} {invitation.metadata.inviterEmail}</>
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('access_shared_project_instruction')}
            </p>
          </div>
          <Login />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1A1F2C] to-[#2d1f3d]">
      <div className="text-center">
        <Loader2 className="h-8 w-4 animate-spin mx-auto mb-4" />
        <p className="text-lg">{t('processing_invitation')}</p>
      </div>
    </div>
  );
}